import { CHAPTER_CONTENT_DELETION_REQUIRES_FORCE } from '@/constants/chapterContentDeletion'
import { CHAPTER_VERSION_CONFLICT } from '@/constants/chapterVersionConflict'
import type { Chapter } from '@/types'
import { normalizeContentVersion, readServerLastContentSaveId } from '@/utils/chapterContentVersion'
import { isConnectivityError } from '@/utils/isConnectivityError'
import { getHttpErrorStatus } from '@/utils/httpErrorStatus'

const DEFAULT_CHAPTER_SYNC_FAILURE = 'No se pudo subir este capítulo.'

const MASS_DELETION_SYNC_FAILURE = 'El cambio borra la mayor parte del contenido. Ábrelo en el editor para confirmar el guardado.'
const VERSION_CONFLICT_SYNC_FAILURE = 'La versión del servidor cambió mientras editabas sin conexión.'
export const CHAPTER_POSSIBLE_VERSION_CONFLICT = 'POSSIBLE_VERSION_CONFLICT'
const POSSIBLE_VERSION_CONFLICT_SYNC_FAILURE =
  'No se pudo guardar por un posible conflicto de versiones. Ábrelo en el editor para revisarlo.'

const CHAPTER_SYNC_FAILURE_MESSAGES: Record<string, string> = {
  [CHAPTER_CONTENT_DELETION_REQUIRES_FORCE]: MASS_DELETION_SYNC_FAILURE,
  CHAPTER_CONTENT_DELETION_REQUIRES_FORCE_ERROR: MASS_DELETION_SYNC_FAILURE,
  [CHAPTER_VERSION_CONFLICT]: VERSION_CONFLICT_SYNC_FAILURE,
  CONFLICT_ERROR: VERSION_CONFLICT_SYNC_FAILURE,
  [CHAPTER_POSSIBLE_VERSION_CONFLICT]: POSSIBLE_VERSION_CONFLICT_SYNC_FAILURE,
  NOT_FOUND_ERROR: 'El capítulo ya no existe en el servidor.',
  GONE_ERROR: 'El capítulo fue eliminado.',
  FORBIDDEN_ERROR: 'No tienes permiso para guardar este capítulo.',
  UNAUTHORIZED_ERROR: 'La sesión expiró. Vuelve a entrar para subir este capítulo.',
  SERVICE_UNAVAILABLE_ERROR: 'El servidor no está disponible en este momento.',
  REQUEST_TIMEOUT_ERROR: 'El servidor tardó demasiado en responder.',
  CONNECTIVITY: 'No hay conexión para subir este capítulo.',
}

function getAxiosErrorData(error: unknown): Record<string, unknown> | null {
  const responseData = (error as { response?: { data?: Record<string, unknown> } })?.response?.data
  return responseData && typeof responseData === 'object' ? responseData : null
}

export function isChapterContentDeletionRequiresForceError(error: unknown): boolean {
  const responseData = getAxiosErrorData(error)
  if (!responseData) return false

  const nestedData = responseData.data
  const reasonFromNested = typeof nestedData === 'object' && nestedData !== null
    ? (nestedData as { reason?: string }).reason
    : undefined

  return reasonFromNested === CHAPTER_CONTENT_DELETION_REQUIRES_FORCE
    || responseData.reason === CHAPTER_CONTENT_DELETION_REQUIRES_FORCE
}

/**
 * Detects optimistic concurrency conflict on chapters.patch (HTTP 409).
 * Gateway forwards Moleculer ConflictError as { code, type, data: { reason, chapter } }.
 */
export function isChapterVersionConflictError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409) return true

  const responseData = getAxiosErrorData(error)
  if (!responseData) return false

  if (responseData.code === 409 || responseData.type === 'CONFLICT_ERROR') return true

  const nestedData = responseData.data
  const reasonFromNested = typeof nestedData === 'object' && nestedData !== null
    ? (nestedData as { reason?: string }).reason
    : undefined

  return reasonFromNested === CHAPTER_VERSION_CONFLICT
    || responseData.reason === CHAPTER_VERSION_CONFLICT
}

function readErrorCode(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function statusErrorCode(status: number | null): string | null {
  if (status === 401) return 'UNAUTHORIZED_ERROR'
  if (status === 403) return 'FORBIDDEN_ERROR'
  if (status === 404) return 'NOT_FOUND_ERROR'
  if (status === 408) return 'REQUEST_TIMEOUT_ERROR'
  if (status === 409) return 'CONFLICT_ERROR'
  if (status === 410) return 'GONE_ERROR'
  if (status === 503) return 'SERVICE_UNAVAILABLE_ERROR'
  return null
}

/**
 * Spanish explanation for a failed offline chapter upload.
 * Unknown backend reasons fall back to a generic message.
 */
export function describeChapterSyncFailure(error: unknown): string {
  if (isConnectivityError(error)) {
    return CHAPTER_SYNC_FAILURE_MESSAGES.CONNECTIVITY
  }

  const responseData = getAxiosErrorData(error)
  const nestedData = responseData?.data
  const nestedReason = typeof nestedData === 'object' && nestedData !== null
    ? readErrorCode((nestedData as { reason?: unknown }).reason)
    : null
  const codes = [
    nestedReason,
    responseData ? readErrorCode(responseData.reason) : null,
    responseData ? readErrorCode(responseData.type) : null,
    statusErrorCode(getHttpErrorStatus(error)),
  ]

  for (const code of codes) {
    if (code && CHAPTER_SYNC_FAILURE_MESSAGES[code]) {
      return CHAPTER_SYNC_FAILURE_MESSAGES[code]
    }
  }

  return DEFAULT_CHAPTER_SYNC_FAILURE
}

/** Logs only the contentVersion pair that triggered a chapter conflict. */
export function logChapterContentVersionConflict(
  trigger: 'load' | 'patch',
  localOrExpectedContentVersion: number,
  serverContentVersion: number,
): void {
  const label = trigger === 'load' ? 'localBaseContentVersion' : 'expectedContentVersion'
  console.warn('[Escribonauta chapter conflict]', {
    [label]: localOrExpectedContentVersion,
    serverContentVersion,
  })
}

/** Extracts the current server chapter payload from a 409 conflict error body. */
export function getChapterFromVersionConflictError(error: unknown): Chapter | null {
  const responseData = getAxiosErrorData(error)
  if (!responseData) return null

  const nestedData = responseData.data
  const chapterFromNested = typeof nestedData === 'object' && nestedData !== null
    ? (nestedData as { chapter?: Chapter }).chapter
    : undefined
  const chapter = chapterFromNested
    || (responseData.chapter as Chapter | undefined)

  if (!chapter || typeof chapter !== 'object' || !chapter._id) return null

  const updatedAt = chapter.updatedAt
    ? new Date(chapter.updatedAt).toISOString()
    : undefined

  return {
    _id: String(chapter._id),
    title: chapter.title || '',
    jsonContent: chapter.jsonContent,
    content: chapter.content,
    projectId: chapter.projectId ? String(chapter.projectId) : '',
    userId: chapter.userId ? String(chapter.userId) : '',
    order: typeof chapter.order === 'number' ? chapter.order : 0,
    updatedAt: updatedAt || new Date(0).toISOString(),
    contentVersion: normalizeContentVersion(
      (chapter as { contentVersion?: number | null }).contentVersion,
    ),
    lastContentSaveId: readServerLastContentSaveId(chapter as Chapter),
    contentHash: (chapter as { contentHash?: string | null }).contentHash ?? undefined,
  }
}
